import axios, { AxiosError } from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { orderData, PromoCode } from '../../@types/order.types';
import { baseURL } from 'utils/constants/baseURL';
import toast from 'react-hot-toast';

type ErrorResponse = {
  message: string;
};

axios.defaults.baseURL = baseURL;

const handleError = (error: any): string => {
  if (error.response && error.response.data && error.response.data.message) {
    return `Oops! Something was wrong... ${error.response.data.message}`;
  } else {
    return `Oops! Something was wrong... ${error.message}`;
  }
};

export const addOrder = createAsyncThunk<
  { orderNum: string },
  orderData,
  { rejectValue: string }
>('order/addOrder', async (orderData, thunkApi) => {
  try {
    const { data } = await axios.post<{ orderNum: string }>(
      'order/add-order',
      orderData
    );
    return data;
  } catch (error:any) {
    const errorMessage = handleError(error);
    toast.error('Сталася помилка, спробуйте ще раз', {
      id: 'error',
    });
    return thunkApi.rejectWithValue(errorMessage);
  }
});

// monopay/create has its own, stricter Joi schema for the top-level fields
// (confirmed against the sandbox — tel lives inside userData, `payment` isn't
// part of the schema at all, the controller sets 'monopay_parts' itself).
// cartItems, though, has NO schema on the backend (bare Mongoose Array, Joi
// array().required() with no .items()) — it's stored through verbatim and
// only the admin UI's expectations constrain its shape. So unlike the other
// fields, cartItems must stay byte-for-byte what addOrder sends — any
// reshaping here (e.g. a reduced {id,name,qty,price} projection) produces
// field names the admin UI doesn't recognize, showing "NaN шт" and no photo.
type MonopayCreatePayload = {
  userData: {
    firstName: string;
    lastName: string;
    email: string;
    text: string;
    tel: string;
  };
  total?: number;
  promoCode: string;
  promoCodeDiscount: number;
  discountValue: number;
  together: number;
  cartItems: orderData['cartItems'];
  deliveryType: string;
  city: string;
  warehouse: string;
  payParts: number;
};

export const createMonopayOrder = createAsyncThunk<
  { orderNum: string; status: string },
  orderData & { payParts: number },
  { rejectValue: string }
>('order/createMonopayOrder', async (orderData, thunkApi) => {
  try {
    const payload: MonopayCreatePayload = {
      userData: {
        firstName: orderData.userData.firstName,
        lastName: orderData.userData.lastName,
        email: orderData.userData.email,
        text: orderData.userData.text,
        tel: orderData.userData.tel,
      },
      total: orderData.total,
      promoCode: orderData.promoCode,
      promoCodeDiscount: orderData.promoCodeDiscount,
      discountValue: orderData.discountValue,
      together: orderData.together,
      cartItems: orderData.cartItems,
      deliveryType: orderData.deliveryType,
      city: orderData.city,
      warehouse: orderData.warehouse,
      payParts: orderData.payParts,
    };
    const { data } = await axios.post<{ orderNum: string; status: string }>(
      'monopay/create',
      payload
    );
    return data;
  } catch (error: any) {
    const errorMessage = handleError(error);
    toast.error(
      'Не вдалося створити замовлення на оплату частинами. Спробуйте ще раз або оберіть інший спосіб оплати.',
      { id: 'error-monopay' }
    );
    return thunkApi.rejectWithValue(errorMessage);
  }
});

// acquiring/create mirrors monopay/create's payload contract exactly, minus
// payParts (a one-off card charge has no installments) — same nested userData
// with tel inside, and the same untouched cartItems. Reusing the type instead
// of restating it keeps the two in lockstep: cartItems in particular has no
// backend schema and must stay byte-for-byte what addOrder sends, or the admin
// UI shows "NaN шт" and no photo.
type AcquiringCreatePayload = Omit<MonopayCreatePayload, 'payParts'>;

// Unlike monopay (which confirms in the banking app with no redirect), the
// acquiring flow hands the shopper off to monobank's own payment page: the
// backend creates the invoice and returns its pageUrl for us to navigate to.
export const createAcquiringOrder = createAsyncThunk<
  { orderNum: string; pageUrl: string },
  orderData,
  { rejectValue: string }
>('order/createAcquiringOrder', async (orderData, thunkApi) => {
  try {
    const payload: AcquiringCreatePayload = {
      userData: {
        firstName: orderData.userData.firstName,
        lastName: orderData.userData.lastName,
        email: orderData.userData.email,
        text: orderData.userData.text,
        tel: orderData.userData.tel,
      },
      total: orderData.total,
      promoCode: orderData.promoCode,
      promoCodeDiscount: orderData.promoCodeDiscount,
      discountValue: orderData.discountValue,
      together: orderData.together,
      cartItems: orderData.cartItems,
      deliveryType: orderData.deliveryType,
      city: orderData.city,
      warehouse: orderData.warehouse,
    };
    const { data } = await axios.post<{ orderNum: string; pageUrl: string }>(
      'acquiring/create',
      payload
    );
    // The whole point of this branch is the redirect, so a response without a
    // usable pageUrl is a failure even though the request itself succeeded.
    // Rejecting here (rather than letting the component cope) routes it through
    // the normal rejected path, which clears the loading state — otherwise the
    // full-screen loader held for the redirect would never come down.
    if (!data?.pageUrl) {
      toast.error(
        'Не вдалося відкрити сторінку оплати. Спробуйте ще раз або оберіть інший спосіб оплати.',
        { id: 'error-acquiring' }
      );
      return thunkApi.rejectWithValue('Acquiring response contained no pageUrl');
    }
    return data;
  } catch (error: any) {
    const errorMessage = handleError(error);
    toast.error(
      'Не вдалося створити замовлення на оплату карткою. Спробуйте ще раз або оберіть інший спосіб оплати.',
      { id: 'error-acquiring' }
    );
    return thunkApi.rejectWithValue(errorMessage);
  }
});

// Публічний статус оплати для сторінки /payment/result.
//
// Свідомо ПОЗА slice-ом замовлення: сторінка результату — це вже інший візит
// (клієнт повернувся з сайту банку), кошик і форма на той момент очищені, і
// заводити для неї глобальний стан немає потреби. Компонент тримає відповідь
// у себе, тому тут звичайний axios, а не createAsyncThunk.
//
// Ідентифікатор — невгадуваний ref з query-параметра redirectUrl. Номер
// замовлення бек тут не приймає: він послідовний, і чужий статус діставався б
// перебором.
export type PaymentStatusResponse = {
  orderNum: string;
  status: string;
  failureReason: string | null;
  together: number;
  // Приходить, лише якщо оплата провалилась, а рахунок ще живий — тоді на
  // нього можна відправити клієнта повторно, без нового замовлення.
  pageUrl: string | null;
};

export const fetchPaymentStatus = async (
  ref: string
): Promise<PaymentStatusResponse> => {
  const { data } = await axios.post<PaymentStatusResponse>(
    'acquiring/public-status',
    { ref }
  );

  return data;
};

export const getDeliveryCities = createAsyncThunk<
  { cities: string[] },
  string,
  { rejectValue: string }
  >(
  'order/getDeliveryCity',
  async (reqCity, thunkApi) => {
    const reqData = { query: reqCity };
    try {
      const { data } = await axios.post<{ cities: string[] }>('order/getDeliveryCity', reqData);
      return data;
    } catch (error) {
      const errorMessage = handleError(error);
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const getDeliveryWarehouses = createAsyncThunk<
  { werehouses: string[] },
  string,
  { rejectValue: string }
  >(
  'order/getWarehouses',
  async (reqWarehouses, thunkApi) => {
    try {
      const reqData = { query: reqWarehouses };
      const { data } = await axios.post<{ werehouses: string[] }>('order/getWarehouses', reqData);
      return data;
    } catch (error) {
      const errorMessage = handleError(error);
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const addPromoCode = createAsyncThunk<
  { promoCode: PromoCode },
  string,
  { rejectValue: string }
>('order/addPromoCode', async (name, thunkApi) => {
  try {
    const { data } = await axios.get<{ promoCode: PromoCode }>(
      `order/promo-code/${name}`
    );
    toast.success('Промокод застосовано!');

    return data;
  } catch (err) {
    const error = err as AxiosError<ErrorResponse>;
    const errorMessage = handleError(error);
    console.error(errorMessage);

    const errorResponseMessage = error.response?.data?.message;
    if (errorResponseMessage === 'Bad request') {
      return thunkApi.rejectWithValue('*Промокод недійсний, спробуйте інший!');
    } else if (errorResponseMessage === 'promoCode not valid') {
      return thunkApi.rejectWithValue(
        '*Термін дій промокоду завершився, спробуйте інший!'
      );
    } else if (errorResponseMessage === 'promoCode already in use') {
      return thunkApi.rejectWithValue(
        '*Ви вже використали даний промокод, спробуйте інший!'
      );
    } else {
      toast.error('Сталася помилка, спробуйте ще раз');
      return thunkApi.rejectWithValue('Сталася помилка, спробуйте ще раз!');
    }
  }
});
